
import { useState } from 'react';

interface ManifestModalProps {
  manifest: string;
  onClose: () => void;
  answers: Record<number, { responses: string[] }>;
}

export default function ManifestModal({ manifest, onClose, answers }: ManifestModalProps) {
  const [copied, setCopied] = useState(false);
  const [showAnalysis, setShowAnalysis] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(manifest);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownload = () => {
    const blob = new Blob([manifest], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'Мой_профессиональный_манифест.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const generateFinalAnalysis = () => {
    const completedSteps = Object.keys(answers).length;
    const totalResponses = Object.values(answers).reduce((sum, step) => {
      return sum + step.responses.filter(r => r && r.trim().length > 0).length;
    }, 0);

    return `Ты прошла невероятный путь из ${completedSteps} шагов и дала ${totalResponses} честных ответов себе. Это огромная работа!

**Твой профессиональный рост**
${answers[1] ? 'Ты увидела свой путь — от начала до сегодняшнего дня. Ты помнишь, где было сложно, и видишь, как ты выросла. Это признак зрелости и силы.' : ''}

**Твоя ценность**
${answers[2] ? 'Ты осознала, чем ты реально ценна. Ты знаешь, за что тебя благодарят, и понимаешь свою пользу. Это основа твоей уверенности.' : ''}

**Твой стиль**
${answers[3] ? 'Ты определила свой стиль управления. Ты знаешь, как ты принимаешь решения и ведёшь людей. Это твоя уникальная подпись.' : ''}

**Твои границы**
${answers[4] ? 'Ты назвала то, что больше не готова терпеть. Это не жёсткость — это самоуважение. Ты учишься защищать свою энергию.' : ''}

**Твои победы**
${answers[5] ? 'Ты вспомнила моменты, где ты была сильной. Часто мы не замечаем свои достижения, но ты их увидела. Это важно!' : ''}

**Твоя энергия**
${answers[6] ? 'Ты поняла, что тебя наполняет и что истощает. Это ключ к долгой и счастливой карьере. Береги свою энергию!' : ''}

**Твой голос**
${answers[7] ? 'Ты начала думать о том, как ты говоришь "нет" и как хочешь, чтобы с тобой разговаривали. Твой голос имеет силу!' : ''}

**Твоя уверенность**
${answers[8] ? 'Ты честно посмотрела на свою уверенность — где она есть, где её не хватает. Уверенность — это не отсутствие страха, это умение действовать несмотря на него.' : ''}

**Твоё влияние**
${answers[9] ? 'Ты увидела, как ты меняешь мир вокруг себя. Ты не просто работаешь — ты меняешь жизни людей. Это огромная ценность!' : ''}

**Твои роли**
${answers[10] ? 'Ты осознала свои роли — какие наполняют, какие утомляют. Ты можешь выбирать, какие роли усиливать. Это твоя власть.' : ''}

**Твои желания**
${answers[11] ? 'Ты знаешь, чего хочешь больше и чего меньше. Это твой компас. Следуй за ним!' : ''}

**Твои ресурсы**
${answers[12] ? 'Ты увидела свою опору — внутреннюю и внешнюю. Ты не одна. У тебя есть поддержка!' : ''}

**Твоё будущее**
${answers[13] ? 'Ты представила себя через год. Ты создаёшь своё будущее каждым решением. И оно будет прекрасным!' : ''}

**Твои правила**
${answers[14] ? 'Ты создала свои правила. Они помогут тебе не сбиться с пути. Это твоя система координат.' : ''}

**Твоя сила**
${answers[15] ? 'Ты знаешь, кто ты. Ты выбрала, как жить и работать. Ты видишь свою силу. Это основа всего!' : ''}

---

**Что дальше?**

Ты прошла этот путь не просто так. Каждый ответ — это кирпичик в фундаменте твоей профессиональной силы.

Теперь у тебя есть:
✨ Ясность — ты знаешь, кто ты и чего хочешь
✨ Опора — ты видишь свои ресурсы и границы
✨ Направление — ты понимаешь, куда идёшь
✨ Сила — ты осознаёшь свою ценность

Твой манифест — это не просто текст. Это твоя правда, твоя опора, твой компас.

Возвращайся к нему, когда будет сложно.
Перечитывай, когда будешь сомневаться.
Дополняй, когда будешь расти.

**Ты невероятная. Ты сильная. Ты ценная.**

И теперь ты это знаешь не потому, что кто-то тебе сказал, а потому что ты сама это увидела, прожила и зафиксировала.

Иди вперёд. У тебя всё получится! 💪✨🌸`;
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-3xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#F9659C] to-[#ACE0EB] p-8 rounded-t-3xl text-center">
          <div className="w-24 h-24 mx-auto mb-6 bg-white/20 rounded-full flex items-center justify-center">
            <i className="ri-trophy-fill text-6xl text-white"></i>
          </div>
          <h2 className="text-3xl font-bold text-white mb-3">
            Ты прошла весь путь!
          </h2>
          <p className="text-white/90 text-lg">
            Теперь у тебя есть твой профессиональный манифест
          </p>
        </div>

        {/* Content */}
        <div className="p-8">
          {!showAnalysis ? (
            <>
              {/* Manifest Content */}
              <div className="bg-gradient-to-br from-[#F9659C]/5 to-[#ACE0EB]/5 rounded-2xl p-6 mb-6">
                <pre className="whitespace-pre-wrap font-sans text-gray-800 leading-relaxed text-lg">
                  {manifest}
                </pre>
              </div>

              {/* Show Analysis Button */}
              <div className="text-center mb-6">
                <p className="text-gray-600 text-lg mb-4">
                  Хочешь увидеть полный анализ твоего пути?
                </p>
                <button
                  onClick={() => setShowAnalysis(true)}
                  className="px-8 py-4 bg-gradient-to-r from-[#F9659C] to-[#ACE0EB] text-white rounded-2xl font-medium hover:shadow-lg transition-all whitespace-nowrap cursor-pointer"
                >
                  Показать полный анализ
                </button>
              </div>

              {/* Action Buttons */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                <button
                  onClick={handleDownload}
                  className="px-6 py-4 bg-[#F9659C] text-white rounded-2xl font-medium hover:shadow-lg transition-all flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-download-line text-xl"></i>
                  Скачать
                </button>
                <button
                  onClick={handleCopy}
                  className="px-6 py-4 bg-[#ACE0EB] text-gray-900 rounded-2xl font-medium hover:shadow-lg transition-all flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
                >
                  <i className={`text-xl ${copied ? 'ri-check-line' : 'ri-file-copy-line'}`}></i>
                  {copied ? 'Скопировано!' : 'Скопировать'}
                </button>
              </div>

              {/* Close Button */}
              <button
                onClick={onClose}
                className="w-full px-6 py-4 border-2 border-gray-200 rounded-2xl font-medium text-gray-700 hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
              >
                Закрыть
              </button>
            </>
          ) : (
            <>
              {/* Full Analysis */}
              <div className="bg-gradient-to-br from-[#F9659C]/5 to-[#ACE0EB]/5 rounded-2xl p-6 mb-6">
                <div className="flex items-start gap-3 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-[#F9659C] to-[#ACE0EB] rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-heart-fill text-white text-2xl"></i>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-gray-900 mb-4">
                      Твой путь — это твоя сила
                    </h3>
                    <div className="prose prose-lg max-w-none">
                      <div className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                        {generateFinalAnalysis()}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Back to Manifest Button */}
              <button
                onClick={() => setShowAnalysis(false)}
                className="w-full px-6 py-4 bg-gradient-to-r from-[#F9659C] to-[#ACE0EB] text-white rounded-2xl font-medium hover:shadow-lg transition-all whitespace-nowrap cursor-pointer mb-3"
              >
                Вернуться к манифесту
              </button>

              {/* Close Button */}
              <button
                onClick={onClose}
                className="w-full px-6 py-4 border-2 border-gray-200 rounded-2xl font-medium text-gray-700 hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
              >
                Закрыть
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
